import { Platform } from 'react-native';
import { createApiClient } from '../../../../../api/apiClient';
import { buildOrderRulesRequest } from './requestBuilder';
import { OrderRulesExportReportType, OrderRulesFilterParams } from './types';

// ─── API: POST /api/order-rules/export ───────────────────────────────────────

export interface OrderRulesExportOptions {
  pagination?: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' };
  /** Customer & SKU Utilization tab — selected grouping dimensions. */
  granularity?: string[];
  /** Order Rule Check tab toggles — each is sent only when true, matching the GET request. */
  showNonEligibleSkus?: boolean;
  showReviewed?: boolean;
  showNonInvoicedSkus?: boolean;
}

const DEFAULT_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const parseFilename = (contentDisposition: string | undefined, fallback: string): string => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
  return match?.[1] ? decodeURIComponent(match[1].trim()) : fallback;
};

const decodeErrorMessage = (data: unknown): string | undefined => {
  try {
    if (!(data instanceof ArrayBuffer)) return undefined;
    const text = new TextDecoder('utf-8').decode(new Uint8Array(data));
    return JSON.parse(text)?.message;
  } catch {
    return undefined;
  }
};

const saveFile = (data: ArrayBuffer, filename: string, mimeType: string): void => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    throw new Error('Export download is only supported in the web app.');
  }
  const url = window.URL.createObjectURL(new Blob([data], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

/**
 * Calls the export endpoint with the same payload the tab's data API used, plus `reportType`,
 * then downloads the returned file. pageNumber/pageSize are ignored by the backend (full result set).
 */
export const exportOrderRulesReport = async (
  reportType: OrderRulesExportReportType,
  params: OrderRulesFilterParams,
  locationIds: string[],
  options: OrderRulesExportOptions = {},
): Promise<void> => {
  const body = buildOrderRulesRequest(params, locationIds, options.pagination, {
    includeCustomerMaterialFilters: reportType !== 'max-potential-execution',
    granularity: options.granularity,
  });
  body.reportType = reportType;
  if (options.showNonEligibleSkus) body.showNonEligibleSkus = true;
  if (options.showReviewed) body.showReviewed = true;
  if (options.showNonInvoicedSkus) body.showNonInvoicedSkus = true;

  const api = createApiClient();
  try {
    const res = await api.post<ArrayBuffer>('/order-rules/export', body, { responseType: 'arraybuffer' });
    const headers = res.headers ?? {};
    const mimeType = String(headers['content-type'] ?? DEFAULT_MIME).split(';')[0];
    const filename = parseFilename(headers['content-disposition'], `order-rules-${reportType}.xlsx`);
    saveFile(res.data, filename, mimeType);
  } catch (error: any) {
    const serverMessage = decodeErrorMessage(error?.response?.data);
    if (serverMessage) error.message = serverMessage;
    throw error;
  }
};
